import { BRAND, STATE, EN_ESPERA, REEMBOLSADO } from '../styles/tokens';
import { esCorreoSintetico } from './correoCliente';
import { canalDetallado } from './origenVenta';

// Badges de estado de pedido — derivan de los design tokens (tokens.js / tokens.css).
// Semántica estándar de WooCommerce: pending = sin pago confirmado (NO es venta),
// processing = pagado y por preparar/enviar (venta real), completed = venta cerrada.
export const STATUS_CONFIG = {
  pending:    { label: 'Pendiente de pago',    color: STATE.warning.text, bg: STATE.warning.bg, dark: STATE.warning.text },
  processing: { label: 'Pagado · Por preparar', color: BRAND.primary,      bg: BRAND.primaryLight, dark: BRAND.primary },
  'on-hold':  { label: 'Pago por confirmar',   color: EN_ESPERA.text,     bg: EN_ESPERA.bg,     dark: EN_ESPERA.text },
  completed:  { label: 'Completado',           color: STATE.success.text, bg: STATE.success.bg, dark: STATE.success.text },
  cancelled:  { label: 'Cancelado',            color: STATE.error.text,   bg: STATE.error.bg,   dark: STATE.error.text },
  refunded:   { label: 'Reembolsado',          color: REEMBOLSADO.text,   bg: REEMBOLSADO.bg,   dark: REEMBOLSADO.text },
  failed:     { label: 'Fallido',              color: STATE.error.text,   bg: STATE.error.bg,   dark: STATE.error.text },
};

export const STATUS_OPTIONS = [
  { value: 'any',        label: 'Todos los estados' },
  { value: 'pending',    label: 'Pendiente de pago' },
  { value: 'processing', label: 'Pagado · Por preparar' },
  { value: 'on-hold',    label: 'Pago por confirmar' },
  { value: 'completed',  label: 'Completado' },
  { value: 'cancelled',  label: 'Cancelado' },
  { value: 'refunded',   label: 'Reembolsado' },
];

export function getStatusConfig(status) {
  return STATUS_CONFIG[status] || { label: status || 'Desconocido', color: '#64748B', bg: '#F8FAFC', dark: '#475569' };
}

// Convención CANÓNICA de estado de pedido (F5, Tema 2): UNA sola en toda la app,
// con la variante del kit StatusBadge. Antes vivía sólo en OrdersPage; se
// centraliza acá para que Pedidos, Resumen, Clientes, GlobalSearch y Gestión de
// envíos usen la MISMA (vía el componente <OrderStatusBadge>). Caja queda
// AMPARADA con getStatusConfig/OrderDetailModal hasta que migre en F5#4.
export const STATUS_DEFS = [
  { value: 'completed',  label: 'Completado',         variant: 'success' },
  { value: 'processing', label: 'Por despachar',      variant: 'info' },
  { value: 'pending',    label: 'Pendiente de pago',  variant: 'warning' },
  { value: 'on-hold',    label: 'Pago por confirmar', variant: 'en-espera' },
  { value: 'cancelled',  label: 'Cancelado',          variant: 'error' },
  { value: 'refunded',   label: 'Reembolsado',        variant: 'reembolsado' },
];
export const statusDef = (s) => STATUS_DEFS.find((d) => d.value === s) || { label: s || 'Desconocido', variant: 'neutral' };

export function formatOrderDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export function getCustomerName(order) {
  const fn = order.billing?.first_name || '';
  const ln = order.billing?.last_name  || '';
  const full = `${fn} ${ln}`.trim();
  if (full) return full;
  // U1: si no hay nombre, NO caer al correo SINTÉTICO como nombre (mostraría
  // «{celular}@pos.local»); un correo real sí sirve de identificador.
  const email = order.billing?.email || '';
  return esCorreoSintetico(email) ? '—' : (email || '—');
}

export function fmtMoney(amount, symbol = '$') {
  const n = parseFloat(amount) || 0;
  return `${symbol}${n.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ── Order origin ─────────────────────────────────────────────────────────────

export function getOrderOrigin(order) {
  const meta = order.meta_data || [];
  const isPOS       = meta.some((m) => m.key === 'origen' && m.value === 'POS');
  const isDashboard = meta.some((m) => m.key === '_created_via_dashboard');
  if (isPOS || isDashboard) {
    const canal = meta.find((m) => m.key === 'canal_venta')?.value;
    if (canal === 'Tienda física') return { label: '🏪 Tienda física (POS)', variant: 'pos'       };
    if (canal === 'WhatsApp')      return { label: '💬 WhatsApp (POS)',       variant: 'pos-wa'    };
    if (canal === 'Feria')         return { label: '🎪 Feria (POS)',          variant: 'pos-feria' };
    if (canal)                     return { label: `🎪 ${canal} (POS)`,       variant: 'pos-feria' };
    return { label: '📱 POS (canal no especificado)', variant: 'pos' };
  }
  if (order.created_via === 'checkout') return { label: '🌐 Web',        variant: 'web'     };
  if (order.created_via === 'rest-api') return { label: '🌐 Web (móvil)', variant: 'web'     };
  if (order.created_via === 'admin')    return { label: '⚙️ Admin',       variant: 'admin'   };
  return { label: '❓ Desconocido', variant: 'unknown' };
}

// ── Sales channel normalization ───────────────────────────────────────────────

// Canal de una venta. La `key` es el NOMBRE del canal (coincide con SalesChannel.name y
// con el meta canal_venta guardado en el pedido), para poder filtrar contra la lista
// dinámica de canales configurados — incluidos los eventos. `variant` se conserva para el
// estilo de los canales conocidos.
// diag-origen (19-oct): delega en la DEFINICIÓN ÚNICA (utils/origenVenta, espejada en el backend),
// para que Pedidos, Resumen y Clientes clasifiquen el origen con el MISMO criterio. El comportamiento
// es idéntico al de antes (meta origen=POS/_created_via_dashboard → canal_venta; si no, created_via).
export function normalizeSalesChannel(order) {
  return canalDetallado(order);
}

// ── Payment method label ──────────────────────────────────────────────────────

const PAYMENT_META_KEYS = ['_payment_method_title', '_payment_method'];

export function getMixedPaymentBreakdown(order) {
  const rawValue = (order?.meta_data || []).find((m) => m.key === 'pago_mixto')?.value || '';
  const parts = String(rawValue || '')
    .split('/')
    .map((segment) => {
      const separatorIndex = segment.indexOf(':');
      if (separatorIndex < 0) return null;
      const method = segment.slice(0, separatorIndex).trim();
      const amount = segment.slice(separatorIndex + 1).trim();
      if (!method || !amount) return null;
      return { method, amount };
    })
    .filter(Boolean);
  return { rawValue, parts };
}

export function formatMixedPaymentLabel(order) {
  const mixed = getMixedPaymentBreakdown(order);
  if (!mixed.rawValue) return '';
  if (mixed.parts.length === 0) return `Mixto: ${mixed.rawValue}`;
  return `Mixto: ${mixed.parts.map((part) => `${part.method} ${part.amount}`).join(' / ')}`;
}

export function getPaymentLabel(order) {
  const mixedLabel = formatMixedPaymentLabel(order);
  if (mixedLabel) {
    return { label: mixedLabel, muted: false, mixed: true, parts: getMixedPaymentBreakdown(order).parts };
  }
  if (order.payment_method_title) {
    return { label: order.payment_method_title, muted: false };
  }
  const meta = order.meta_data || [];
  for (const key of PAYMENT_META_KEYS) {
    const found = meta.find((m) => m.key === key);
    if (found?.value) return { label: found.value, muted: false };
  }
  if (order.created_via === 'admin') {
    return { label: 'Manual (sin especificar)', muted: true };
  }
  return { label: 'No registrado', muted: true };
}

// Venta real = pago confirmado (convención estándar WooCommerce): processing + completed.
// Excluye 'pending' (pendiente de pago) y 'on-hold' (pago por confirmar).
export const REVENUE_STATUSES = new Set(['processing', 'completed']);

// Mismo criterio para el Resumen de ventas.
export const RESUMEN_STATUSES = new Set(['processing', 'completed']);

// Colombia is always UTC-5 (no DST). All comparisons use date_created from WooCommerce,
// which is stored in site local time (Bogota). We compare date strings directly to avoid
// JavaScript Date timezone ambiguity across different browser locales.
const COL_OFFSET_MS = 5 * 60 * 60 * 1000;

export function computeOrderKpis(orders) {
  const pad = (n) => String(n).padStart(2, '0');

  // Compute "now" in Colombia time using a UTC-shifted Date so getUTC* methods give Colombia values
  const colNow = new Date(Date.now() - COL_OFFSET_MS);
  const todayStr     = colNow.toISOString().slice(0, 10);
  const weekAgoStr   = new Date(colNow.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const monthStartStr = `${colNow.getUTCFullYear()}-${pad(colNow.getUTCMonth() + 1)}-01`;

  const revenue = orders.filter((o) => REVENUE_STATUSES.has(o.status));
  const sum = (arr) => arr.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);

  // date_created de WooCommerce viene en hora del SITIO, que hoy está configurado como
  // el offset fijo 'UTC-5' (NO como la zona nombrada 'America/Bogota' — decía eso acá y
  // era falso). Mientras el sitio siga en UTC-5, hora del sitio == hora Colombia y
  // recortar a 10 caracteres da 'YYYY-MM-DD' en Colombia, comparable como string.
  // Esa igualdad depende del ajuste de WordPress, no de nuestro código: si alguna vez
  // pasa a zona nombrada, va emparejado con colTime/colTimeUtils (deuda 25).
  const today = revenue.filter((o) => o.date_created?.slice(0, 10) === todayStr);
  const week  = revenue.filter((o) => (o.date_created?.slice(0, 10) ?? '') >= weekAgoStr);
  const month = revenue.filter((o) => (o.date_created?.slice(0, 10) ?? '') >= monthStartStr);

  return {
    todayCount:      today.length,
    todayTotal:      sum(today),
    weekCount:       week.length,
    weekTotal:       sum(week),
    monthCount:      month.length,
    monthTotal:      sum(month),
    avgTicket:       month.length > 0 ? sum(month) / month.length : 0,
    pendingDispatch: orders.filter((o) => o.status === 'processing').length,
  };
}
